import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { useSelectedHospital } from '../SelectedHospitalContext'
import { ApiError } from '../api/client'
import {
  SERVICE_REQUESTS_KEY,
  fetchServiceRequests,
  type AdminServiceRequest,
} from '../api/serviceRequests'
import { SEARCHABLE } from '../searchable'
import { useServerSearch } from '../TableFilterContext'
import { DataTable, type Column } from '../components/DataTable'
import { EmptyState } from '../components/EmptyState'
import { FilterTabs, type FilterTab } from '../components/FilterTabs'
import { StatusPill } from '../components/StatusPill'
import { formatPesewas } from '../../lib/money'


/**
 * Diagnostics (M23-T07), drawn on `1182:5906`. Formerly "Service requests"; the
 * frame's rename is right — the page lists scans, tests and procedures, and
 * "Requests" says nothing about which of the four request queues it is. The
 * route stays `/admin/service-requests` so no link breaks.
 *
 * amtc_admin sees every request; a hospital_admin sees only their hospital's
 * (scoped server-side). Read-only — patient-triggered release/cancel is Stage 2.
 */

/**
 * The API's own vocabulary, not the frame's.
 *
 * The frame's tabs read "Pending Payment / Secured / Completed / Cancelled"
 * while every row beneath them is chipped `Pending` (M23 defect D10). A chip
 * and a tab naming the same state must use the same word, so both come from
 * `service_requests.status` — draft, pending_payment, held, released,
 * cancelled, expired.
 */
const STATUSES = [
  'pending_payment',
  'held',
  'released',
  'cancelled',
  'expired',
] as const

function when(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

export function ServiceRequestsPage(): ReactNode {
  const { token } = useAuth()
  // amtc_admin's "Managing" picker scopes the queue to one hospital; null = all.
  // A hospital_admin has no picker (null) and is scoped server-side anyway.
  const { hospitalId } = useSelectedHospital()
  const [status, setStatus] = useState<string | null>(null)

  const { term, setResultCount } = useServerSearch(SEARCHABLE.diagnostics)
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: [...SERVICE_REQUESTS_KEY, hospitalId, term],
    queryFn: () => fetchServiceRequests(token, hospitalId, term),
    enabled: Boolean(token),
  })

  const all = useMemo(() => data ?? [], [data])
  useEffect(() => {
    setResultCount(data === undefined ? null : data.length)
  }, [data, setResultCount])

  // Counts are real here: the endpoint returns the whole queue rather than a
  // page of it, so these are the actual totals and not a guess over a slice.
  const tabs: FilterTab<string | null>[] = useMemo(() => {
    const present = STATUSES.filter((s) => all.some((r) => r.status === s))
    return [
      { key: null, label: 'All', count: all.length },
      ...present.map((s) => ({
        key: s,
        label: s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        count: all.filter((r) => r.status === s).length,
      })),
    ]
  }, [all])

  const rows = status === null ? all : all.filter((r) => r.status === status)

  const columns: Column<AdminServiceRequest>[] = [
    {
      key: 'reference',
      header: 'Reference',
      cell: (r) => <span className="font-mono text-[13px]">{r.reference}</span>,
    },
    {
      key: 'procedure',
      header: 'Procedure',
      cell: (r) => (
        <>
          <span className="block text-ink-1">{r.procedure_name}</span>
          <span className="block text-xs text-ink-3">{r.hospital_name}</span>
        </>
      ),
    },
    {
      key: 'patient',
      header: 'Patient',
      cell: (r) => (
        <>
          <span className="block text-ink-1">{r.patient_name ?? '—'}</span>
          {r.patient_phone ? (
            <span className="block whitespace-nowrap text-xs text-ink-3">
              {r.patient_phone}
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: 'scheduled',
      header: 'Scheduled',
      cell: (r) => (
        <span className="whitespace-nowrap tabular-nums">
          {when(r.scheduled_for)}
        </span>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      cell: (r) => (
        <span className="tabular-nums">{formatPesewas(r.price_pesewas)}</span>
      ),
    },
    { key: 'status', header: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  ]

  return (
    <section className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Diagnostics
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Scans, tests and procedures patients have requested. A booking is only
          live once payment is recorded.
          {hospitalId ? ' Filtered to the hospital you’re managing.' : ''}
        </p>
      </div>

      {/* The frame draws a "Record Payment" button on every row, including ones
          it labels Pending. There is no record-payment mutation on this page —
          payment is recorded by the Paystack webhook — so it is NOT shipped.
          A button that cannot do anything is worse than an absent one. */}
      <FilterTabs tabs={tabs} active={status} onChange={setStatus} />

      <DataTable
        rows={rows}
        columns={columns}
        getKey={(r) => r.reference}
        searchText={(r) =>
          `${r.reference} ${r.procedure_name} ${r.hospital_name} ${r.patient_name ?? ''} ${r.patient_phone ?? ''}`
        }
        caption="Diagnostics requests"
        isPending={isPending}
        isError={isError}
        {...(error instanceof ApiError && { errorMessage: error.message })}
        onRetry={() => void refetch()}
        empty={
          <EmptyState title="No service requests yet.">
            {status === null
              ? 'Requests appear here as patients book scans and procedures.'
              : 'Nothing in this status. Try All.'}
          </EmptyState>
        }
      />
    </section>
  )
}
